// MA-C9 — `take_away` concept visual for two-operand subtraction.
//
// Renders `a` objects in a 5-per-row grid with the LAST `b` of them
// crossed out (greyed circle + red × mark), depicting "start with a,
// take b away." The first (a − b) intact orange circles are the
// manipulative the child uses to derive the remaining count.
//
// NO numeral shows the answer — the child counts the uncrossed circles.
//
// MA-647 (2026-09-13) — THIS RENDERER IS THE CONCRETE STAGE (see
// mathVisualStage.ts), and the founder's finding was that the concrete
// crossed-out-dots picture never told the child WHAT it modeled: no
// equation, no story sentence, just a counting task wearing a subtraction
// prompt. The fix draws an OPERAND CAPTION above the dots — two numeral
// cards and the operator (`16 − 7`), reusing StagedMathRenderers.tsx's
// `OperandCaption`/`numeralCard` so the card styling isn't duplicated. NO
// "=" and NO answer slot — exactly the EquationRenderer contract. The
// caption reads only `a` and `b` (this component still destructures just
// `{ a, b }`, same as before MA-647); the operator is hardcoded "−" since
// this renderer only ever draws subtraction — never a prop-driven `op`, so
// there is no path here that can read or print the answer.
//
// ADDITIVE — registers itself under the "take_away" kind (see bottom of
// file). The mapper (contentToEngineItem.ts) emits this kind only when
// op==="-" AND a−b===total AND both operands are finite AND b>0 AND the
// prompt has no blank operand (missing-subtrahend suppressed). No edit to
// MathVisual or the session screens; the registry resolves by key.
//
// PROPS (MathRendererProps contract):
//   a     — total objects (minuend); clamped to MAX_A
//   b     — objects taken away (subtrahend)
//   total — remaining count (a − b); NEVER rendered as a numeral
//   op    — always "-" for this renderer (not used internally)
import type { MathRendererProps } from "./rendererRegistry";
import { OperandCaption, operandCaptionSize } from "./StagedMathRenderers";

const DOT_INTACT = "var(--pal-orange, #d97742)";
const DOT_REMOVED_BG = "var(--color-bg-secondary, #f5efe3)";
const DOT_REMOVED_STROKE = "var(--color-border-secondary, #cdbfae)";
const X_COLOR = "var(--color-error, #c0392b)";

const R = 13;       // dot radius (matches Dots() in rendererRegistry.tsx)
const CELL = 34;    // pitch between dot centers (matches Dots())
const PAD = 18;     // first dot center offset (matches Dots())
const COLS = 5;     // dots per row
const MAX_A = 20;   // safety clamp — prevents runaway SVG for bad payload
const CAPTION_GAP = 14; // MA-647 — space between the operand caption and the dots

function TakeAwayRenderer({ a, b }: MathRendererProps) {
  const safeA = Math.max(
    0,
    Math.min(MAX_A, Math.floor(Number.isFinite(a ?? NaN) ? (a as number) : 0)),
  );
  const safeB = Math.max(
    0,
    Math.min(safeA, Math.floor(Number.isFinite(b ?? NaN) ? (b as number) : 0)),
  );

  const rows = Math.max(1, Math.ceil(safeA / COLS));
  const cols = Math.min(COLS, safeA || 1);
  const dotsWidth = PAD * 2 + (cols - 1) * CELL;
  const dotsHeight = PAD * 2 + (rows - 1) * CELL;

  // MA-647 — operand caption above the dots: `safeA − safeB`, no "=" and no
  // answer slot (see StagedMathRenderers.tsx's OperandCaption doc comment).
  const cap = operandCaptionSize();
  const svgWidth = Math.max(dotsWidth, cap.width);
  const svgHeight = cap.height + CAPTION_GAP + dotsHeight;
  const dotsOffsetX = (svgWidth - dotsWidth) / 2;
  const capOffsetX = (svgWidth - cap.width) / 2;
  const dotsOffsetY = cap.height + CAPTION_GAP;

  // Last `safeB` objects are removed (crossed out).
  const firstRemoved = safeA - safeB;

  const xArm = (R * 0.6).toFixed(1);

  return (
    <svg
      viewBox={`0 0 ${svgWidth} ${svgHeight}`}
      width="100%"
      style={{ maxWidth: Math.min(360, svgWidth) }}
      role="img"
      aria-label={`${safeA} take away ${safeB}. ${safeA} objects with ${safeB} taken away.`}
    >
      <OperandCaption a={safeA} b={safeB} op="-" x={capOffsetX} y={0} />
      <g transform={`translate(${dotsOffsetX}, ${dotsOffsetY})`}>
        {Array.from({ length: safeA }).map((_, i) => {
          const cx = PAD + (i % COLS) * CELL;
          const cy = PAD + Math.floor(i / COLS) * CELL;
          const removed = i >= firstRemoved;
          if (removed) {
            return (
              <g key={i}>
                <circle
                  cx={cx}
                  cy={cy}
                  r={R}
                  fill={DOT_REMOVED_BG}
                  stroke={DOT_REMOVED_STROKE}
                  strokeWidth="1.5"
                />
                <line
                  x1={cx - +xArm}
                  y1={cy - +xArm}
                  x2={cx + +xArm}
                  y2={cy + +xArm}
                  stroke={X_COLOR}
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
                <line
                  x1={cx + +xArm}
                  y1={cy - +xArm}
                  x2={cx - +xArm}
                  y2={cy + +xArm}
                  stroke={X_COLOR}
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
              </g>
            );
          }
          return <circle key={i} cx={cx} cy={cy} r={R} fill={DOT_INTACT} />;
        })}
      </g>
    </svg>
  );
}

export default TakeAwayRenderer;
